import PrimaryButton from 'components/common/PrimaryButton';
import React, { useEffect, useState } from 'react'
import styles from 'styles/components/ProfilePlanSummaryCard.module.sass';
import { resumeSubscription } from 'api/SubscriptionApiService';
import { useToast } from 'provider/ToastProvider';


/**
 * Props for the ProfilePlanSummaryCard component.
 */
interface ProfilePlanSummaryCardProps {
    /**
     * The name of the plan.
     */
    planName: string;

    /**
     * A description of the plan.
     */
    planDescription: string;

    /**
     * Callback function to handle subscription changes.
     */
    onChangeSubscription: () => void;
    /**
     * Indicates whether the subscription is scheduled for cancellation at the end of the current period.
     * If true, the subscription is set to cancel at the end of the period.
     * If false, the subscription is not set to cancel.
     */
    scheduledCancellation?: boolean;

    /**
     * True for Pilot-plan users. Pilot has no real subscription to change or
     * cancel (no Stripe subscription exists), and Pilot participants are
     * never shown subscription-change/payment prompts during the program -
     * this replaces the Change/Reactivate button with a Feedback button.
     */
    isPilot?: boolean;

    /**
     * Callback for the Feedback button shown to Pilot users instead of
     * Change/Reactivate Subscription.
     */
    onGiveFeedback?: () => void;
}


/**
 * ProfilePlanSummaryCard component displays the current subscription plan details
 * and provides an option to change the subscription.
 *
 * @component
 * @param {ProfilePlanSummaryCardProps} props - The properties object.
 * @param {string} props.planName - The name of the current plan.
 * @param {string} props.planDescription - A brief description of the current plan.
 * @param {() => void} props.onChangeSubscription - Callback function to handle subscription change.
 *
 * @example
 * <ProfilePlanSummaryCard
 *   planName="Premium Plan"
 *   planDescription="Access to all premium features"
 *   onChangeSubscription={() => console.log('Change subscription clicked')}
 * />
 */
const ProfilePlanSummaryCard: React.FC<ProfilePlanSummaryCardProps> = ({ planName, planDescription, scheduledCancellation, onChangeSubscription, isPilot, onGiveFeedback }) => {
    const [planColor, setPlanColor] = useState<string>('#216f97')
    const { addToast } = useToast();

    useEffect(() => {
        switch (planName.toLowerCase()) {
            case 'basic':
                setPlanColor('#216f97')
                break
            case 'sapphire':
                setPlanColor('#003FD1')
                break
            case 'gold':
                setPlanColor('#E88B00')
                break
            case 'platinum':
                setPlanColor('#BCBCBC')
                break
            case 'pilot':
                setPlanColor('#7C3AED')
                break
            default :
                setPlanColor('#707070')
                break
        }
    }, [planName])

    const onReactivateSubscription = () => {
        addToast({
            type: 'info',
            message: 'Resuming subscription...',
        })

        resumeSubscription({ tag: 'ResumeSubscription' })
        .then((result) => {
            if (!result || !result.success || !result.data) {
                addToast({
                    type: 'error',
                    message: 'Failed to resume subscription. Please try again later.',
                })
                return;
            }
            if (result.data.status !== 'active') {
                addToast({
                    type: 'error',
                    message: 'Subscription is not active. Please contact support.',
                })
                return;
            }
            
            addToast({
                type: 'success',
                message: 'We have successfully reactivated your subscription. Future billing will resume as scheduled.',
            })

            /**
             * Trigger auth change event to update user subscription.
             * This will ensure that the user's subscription status is updated across the app.
             * 
             * Navigate to home page after successful subscription change.
             * TODO: Refactor this to use a more robust state management solution in the future.
             */
            window.dispatchEvent(new Event('authChange'));
        }).catch(() => {
            addToast({
                type: 'error',
                message: 'An error occurred while resuming your subscription. Please try again later.',
            })
        })
    }

    return (
        <div className={styles['profile-plan-summary-card']}>
            <p className={styles['plan-summary-title']}>Current Plan</p>
            <h2 className={styles['plan-name']} style={{ color: planColor }}>{planName}</h2>
            <p className={styles['plan-description']}>{planDescription}</p>
            <PrimaryButton
                text={isPilot ? 'Give Feedback' : scheduledCancellation ? 'Reactivate Subscription' : 'Change Subscription'}
                primaryColor='#FF5522'
                textColor='#FFFFFF'
                hoverColor='#FFFFFF'
                hoverTextColor='#FF5522'
                icon={''}
                doSomething={isPilot ? (onGiveFeedback ?? (() => {})) : scheduledCancellation ? onReactivateSubscription : onChangeSubscription}
                width='100%'
            />
        </div>
    )
}

export default ProfilePlanSummaryCard;